import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import {
  Button,
  ButtonVariant,
  Typography,
  TypographyVariant,
} from "../../../../../components/ui-kit";
import { useAuthUserContext } from "../../../../../context/AdministratorContextProvider";
import {
  NotificationTypes,
  useNotification,
} from "../../../../../hooks/useNotification";
import { getApiErrorMessageWithForbiddenFallback } from "../../../../../util/apiErrorMessage";
import { useCompanyCryptoWalletFeeSettings } from "./useCompanyCryptoWalletFeeSettings";
import { CryptoFeeTariffFields } from "../../../billing/components/crypto-wallet-fees/CryptoFeeTariffFields";
import { CryptoFeeWalletFields } from "../../../billing/components/crypto-wallet-fees/CryptoFeeWalletFields";
import {
  CRYPTO_WALLET_ADMIN_PROVIDERS,
  CRYPTO_WALLET_PROVIDER_CROSSMINT,
  sanitizePercentageInput,
  sanitizeStablecoinAmountInput,
} from "../../../billing/components/crypto-wallet-fees/cryptoWalletFeeSettingsForm";
import {
  buildCompanyCryptoWalletFeeOverridePatchPayload,
  companyCryptoWalletOverrideToDraft,
  companyCryptoWalletProviderLabel,
  createDefaultCompanyCryptoWalletFeeOverrideDraft,
} from "./companyCryptoWalletFeeSettingsForm";

const Card = styled.div`
  margin-top: 20px;
  background: #ffffff;
  border: 1px solid #eeeeee;
  border-radius: 10px;
  padding: 20px;
`;

const Actions = styled.div`
  margin-top: 16px;
  display: flex;
  gap: 10px;
`;

const SectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 12px 0;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const ProviderRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

const ProviderSelectLabel = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
`;

const ProviderSelect = styled.select`
  min-width: 200px;
  height: 36px;
  border-radius: 5px;
  border: 1px solid #dbe3eb;
  padding: 0 12px;
  font-family: Montserrat, sans-serif;
  font-size: 14px;
  color: #0e233e;
  background: #fff;
  outline: none;
  &:focus {
    border-color: #027aff;
    box-shadow: 0 0 0 3px rgba(2, 120, 255, 0.153);
  }
`;

const ModeSection = styled.div`
  margin-top: 14px;
  max-width: 350px;
`;

const NativeSelect = styled.select`
  width: 100%;
  height: 36px;
  border-radius: 5px;
  border: 1px solid #dbe3eb;
  padding: 0 12px;
  font-family: Montserrat, sans-serif;
  font-size: 14px;
  color: #0e233e;
  background: #fff;
  outline: none;
  &:focus {
    border-color: #027aff;
    box-shadow: 0 0 0 3px rgba(2, 120, 255, 0.153);
  }
`;

const Helper = styled(Typography).attrs({
  variant: TypographyVariant.BODY7,
})`
  color: #6b7280;
  margin-top: 8px;
`;

const runtimeEnv = (process.env.REACT_APP_ENV ?? "").toLowerCase();
const isProductionEnvironment =
  runtimeEnv === "production" || runtimeEnv === "prod";

const CompanyCryptoWalletFeeSettings: React.FC<{ companyId?: string }> = ({
  companyId,
}) => {
  const { showNotification } = useNotification();
  const { authUser } = useAuthUserContext();
  const canEditCompany = !!authUser?.policies?.actionsOnWalletFeeSettings;

  const [selectedProvider, setSelectedProvider] = useState(
    CRYPTO_WALLET_PROVIDER_CROSSMINT
  );
  const [overrideDraft, setOverrideDraft] = useState(() =>
    createDefaultCompanyCryptoWalletFeeOverrideDraft(
      CRYPTO_WALLET_PROVIDER_CROSSMINT
    )
  );
  const [showProductionWalletOverride, setShowProductionWalletOverride] =
    useState(isProductionEnvironment);

  const { override, loading, error, patchOverride, saving } =
    useCompanyCryptoWalletFeeSettings(companyId, selectedProvider);

  const selectedProviderLabel = useMemo(
    () => companyCryptoWalletProviderLabel(selectedProvider),
    [selectedProvider]
  );

  useEffect(() => {
    if (loading) return;
    setOverrideDraft(
      companyCryptoWalletOverrideToDraft(override, selectedProvider)
    );
  }, [loading, override, selectedProvider]);

  useEffect(() => {
    if (!error) return;
    showNotification(
      getApiErrorMessageWithForbiddenFallback(
        error,
        "Could not load crypto wallet fee override for this company"
      ),
      NotificationTypes.WARNING
    );
  }, [error, showNotification]);

  useEffect(() => {
    if (isProductionEnvironment) {
      setShowProductionWalletOverride(true);
    }
  }, []);

  const saveCryptoWalletFeeOverride = async (): Promise<void> => {
    if (!companyId) {
      return;
    }
    if (!canEditCompany) {
      showNotification(
        "You do not have permission to edit company wallet fee settings",
        NotificationTypes.WARNING
      );
      return;
    }

    const { payload, error } =
      buildCompanyCryptoWalletFeeOverridePatchPayload(overrideDraft);
    if (!payload || error) {
      showNotification(
        error ?? "Crypto wallet fee override is invalid",
        NotificationTypes.WARNING
      );
      return;
    }

    try {
      const saved = await patchOverride(payload);
      setOverrideDraft(
        companyCryptoWalletOverrideToDraft(saved, selectedProvider)
      );
      showNotification(
        "Crypto wallet fee override saved",
        NotificationTypes.SUCCESS
      );
    } catch (error: unknown) {
      showNotification(
        getApiErrorMessageWithForbiddenFallback(error, "Save failed"),
        NotificationTypes.DANGER
      );
    }
  };

  if (!companyId) {
    return (
      <Typography variant={TypographyVariant.BODY1}>
        Company id is missing.
      </Typography>
    );
  }

  if (loading) {
    return (
      <Typography variant={TypographyVariant.BODY1}>
        Loading crypto wallet fee settings...
      </Typography>
    );
  }

  return (
    <Card>
      <SectionTitle>Crypto wallet fees ({selectedProviderLabel})</SectionTitle>

      <ProviderRow>
        <ProviderSelectLabel>Crypto wallet provider</ProviderSelectLabel>
        <ProviderSelect
          aria-label="Crypto wallet provider for company fee override"
          value={selectedProvider}
          disabled={saving}
          onChange={(event) => setSelectedProvider(event.target.value)}
        >
          {CRYPTO_WALLET_ADMIN_PROVIDERS.map((provider) => (
            <option key={provider.value} value={provider.value}>
              {provider.label}
            </option>
          ))}
        </ProviderSelect>
      </ProviderRow>

      <Typography variant={TypographyVariant.BODY7} color="#6b7280">
        Configure optional company-level overrides. Fees and wallets can inherit
        global defaults or be set as custom values per environment. Runtime
        applies the wallet for the active environment only.
      </Typography>

      <ModeSection>
        <Typography
          variant={TypographyVariant.BODY7}
          style={{ marginBottom: 5 }}
        >
          Company crypto fee enabled mode
        </Typography>
        <NativeSelect
          aria-label="Company crypto fee enabled mode"
          value={overrideDraft.enabledMode}
          disabled={!canEditCompany || saving}
          onChange={(event) =>
            setOverrideDraft((prev) => ({
              ...prev,
              enabledMode: event.target.value as typeof prev.enabledMode,
            }))
          }
        >
          <option value="inherit">Inherit platform enabled setting</option>
          <option value="enabled">Force enabled</option>
          <option value="disabled">Force disabled</option>
        </NativeSelect>
      </ModeSection>

      <ModeSection>
        <Typography
          variant={TypographyVariant.BODY7}
          style={{ marginBottom: 5 }}
        >
          Company crypto fee tariff mode
        </Typography>
        <NativeSelect
          aria-label="Company crypto fee tariff mode"
          value={overrideDraft.tariffMode}
          disabled={!canEditCompany || saving}
          onChange={(event) =>
            setOverrideDraft((prev) => ({
              ...prev,
              tariffMode: event.target.value as typeof prev.tariffMode,
            }))
          }
        >
          <option value="inherit">Inherit platform tariff</option>
          <option value="custom">Set custom tariff override</option>
        </NativeSelect>
      </ModeSection>

      <CryptoFeeTariffFields
        sectionTitle="Tariff overrides"
        sectionDescription="Use custom event overrides only when this company requires non-standard rates."
        defaultLabel="Default override"
        defaultOverrideToggleLabel="Enable default tariff override"
        defaultOverrideEnabled={overrideDraft.defaultTariffEnabled}
        onDefaultOverrideEnabledChange={(enabled) =>
          setOverrideDraft((prev) => ({
            ...prev,
            defaultTariffEnabled: enabled,
          }))
        }
        defaultDraft={overrideDraft.defaultTariff}
        onDefaultDraftChange={(next) =>
          setOverrideDraft((prev) => ({
            ...prev,
            defaultTariff: next,
          }))
        }
        eventDrafts={overrideDraft.eventTariffs}
        onEventDraftChange={(event, next) =>
          setOverrideDraft((prev) => ({
            ...prev,
            eventTariffs: {
              ...prev.eventTariffs,
              [event]: next,
            },
          }))
        }
        sanitizePercentageInput={sanitizePercentageInput}
        sanitizeFlatFeeInput={sanitizeStablecoinAmountInput}
        disabled={
          !canEditCompany || saving || overrideDraft.tariffMode !== "custom"
        }
      />

      <CryptoFeeWalletFields
        sectionTitle="Sandbox wallet override"
        sectionDescription="Applies only to this company in sandbox environment."
        mode={overrideDraft.walletSandboxMode}
        onModeChange={(mode) =>
          setOverrideDraft((prev) => ({
            ...prev,
            walletSandboxMode: mode,
          }))
        }
        inheritLabel="Use global sandbox wallet"
        customLabel="Set custom sandbox wallet"
        value={overrideDraft.walletSandbox}
        onChange={(next) =>
          setOverrideDraft((prev) => ({
            ...prev,
            walletSandbox: next,
          }))
        }
        disabled={!canEditCompany || saving}
        selectMaxWidth={350}
      />

      {!isProductionEnvironment && (
        <Helper as="p">
          Production wallet override is optional on this environment and applies
          only when merchants run on Crossmint production.
        </Helper>
      )}

      {showProductionWalletOverride ? (
        <>
          <CryptoFeeWalletFields
            sectionTitle="Production wallet override"
            sectionDescription={
              isProductionEnvironment
                ? "Applies only to this company in production environment."
                : "Optional for current environment. Used only for production merchants."
            }
            mode={overrideDraft.walletProductionMode}
            onModeChange={(mode) =>
              setOverrideDraft((prev) => ({
                ...prev,
                walletProductionMode: mode,
              }))
            }
            inheritLabel="Use global production wallet"
            customLabel="Set custom production wallet"
            value={overrideDraft.walletProduction}
            onChange={(next) =>
              setOverrideDraft((prev) => ({
                ...prev,
                walletProduction: next,
              }))
            }
            disabled={!canEditCompany || saving}
            selectMaxWidth={350}
          />
          {!isProductionEnvironment && (
            <Actions>
              <Button
                variant={ButtonVariant.TEXT}
                type="button"
                disabled={saving}
                onClick={() => setShowProductionWalletOverride(false)}
              >
                Hide production wallet override
              </Button>
            </Actions>
          )}
        </>
      ) : (
        <Actions>
          <Button
            variant={ButtonVariant.OUTLINED}
            type="button"
            disabled={saving}
            onClick={() => setShowProductionWalletOverride(true)}
          >
            Show production wallet override
          </Button>
        </Actions>
      )}

      <Actions>
        <Button
          variant={ButtonVariant.CONTAINED}
          type="button"
          disabled={saving || !canEditCompany}
          onClick={() => void saveCryptoWalletFeeOverride()}
        >
          Save crypto wallet fee override
        </Button>
      </Actions>

      {!canEditCompany && (
        <Helper as="p">
          You have view access only. Editing company crypto wallet fee settings
          requires permission to manage company wallet fee settings.
        </Helper>
      )}
    </Card>
  );
};

export default CompanyCryptoWalletFeeSettings;
